/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { SortCriterionDetail } from '#UNSAFE_TableView';
import type { TableColumn } from '../Table';

/**
 * Helper function to get the row action context from row information
 */
export const getActionContext = <K extends string | number, D>(_index: number, key: K, data: D) => {
  return { key, data };
};

/**
 * Helper function to determine if a pointer or keyboard event should trigger a sort action gesture
 */
export const isSortActionGesture = <K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  columnKey: C
) => {
  for (const column of columnsArray) {
    if (column.key === columnKey) {
      return column.value.sortable === 'enabled';
    }
  }
  return false;
};

/**
 * Helper function to handle a sort action gesture
 */
export const handleSortActionGesture = <K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  columnKey: C,
  onSortCriterionChange: (detail: SortCriterionDetail<C>) => void,
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[]
) => {
  if (isSortActionGesture(columnsArray, columnKey)) {
    let sortDirection;
    if (sortCriterion != null) {
      for (const criterion of sortCriterion) {
        if (criterion.key === columnKey) {
          sortDirection = criterion.sortDirection;
          break;
        }
      }
    }
    const newDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
    onSortCriterionChange([{ key: columnKey, sortDirection: newDirection }]);
    return true;
  }
  return false;
};
